import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { planningAPI } from "../services/planningService";
import NotificationsHeader from "./notifications/NotificationsHeader";
import NotificationsList from "./notifications/NotificationsList";

const PlanningNotifications = ({ compact = false }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllDialog, setShowAllDialog] = useState(false);
  const [filter, setFilter] = useState("unread");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [filter, typeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadNotifications(), loadSummary()]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const filters = { limit: compact ? 5 : 50 };
      if (filter === "unread") filters.unread_only = true;
      if (typeFilter !== "all") filters.notification_type = typeFilter;
      const data = await planningAPI.notifications.getUserNotifications(filters);
      setNotifications(data.items || []);
    } catch (error) {
    }
  };

  const loadSummary = async () => {
    try {
      const data = await planningAPI.notifications.getNotificationsSummary();
      setSummary(data);
    } catch (error) {
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await planningAPI.notifications.markNotificationAsRead(notificationId);
      await loadData();
    } catch (error) {
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.length === 0) { toast.error(t("planning.legacy.notifications.errors.noneSelected")); return; }
    try {
      await planningAPI.notifications.markMultipleAsRead(selectedNotifications);
      await loadData();
      setSelectedNotifications([]);
    } catch (error) {
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await planningAPI.notifications.markAllAsRead();
      await loadData();
    } catch (error) {
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await planningAPI.notifications.deleteNotification(notificationId);
      await loadData();
    } catch (error) {
    }
  };

  const handleDeleteAllRead = async () => {
    try {
      await planningAPI.notifications.deleteAllRead();
      await loadData();
    } catch (error) {
    }
  };

  const handleToggleSelection = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev.includes(notificationId) ? prev.filter((id) => id !== notificationId) : [...prev, notificationId]
    );
  };

  if (compact) {
    return (
      <div className="relative">
        <Button variant="ghost" size="sm" className="relative" onClick={() => setShowAllDialog(true)}>
          <Bell className="h-5 w-5" />
          {summary && summary.unread_count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {summary.unread_count > 9 ? "9+" : summary.unread_count}
            </Badge>
          )}
        </Button>
        <Dialog open={showAllDialog} onOpenChange={setShowAllDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                {t("planning.legacy.notifications.title")}
              </DialogTitle>
              <DialogDescription>
                {summary && t("planning.legacy.notifications.dialogSummary", { unread: summary.unread_count, total: summary.total_count })}
              </DialogDescription>
            </DialogHeader>
            <PlanningNotifications compact={false} />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NotificationsHeader
        filter={filter}
        setFilter={setFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        selectedNotifications={selectedNotifications}
        handleMarkSelectedAsRead={handleMarkSelectedAsRead}
        handleMarkAllAsRead={handleMarkAllAsRead}
        handleDeleteAllRead={handleDeleteAllRead}
      />

      <NotificationsList
        loading={loading}
        notifications={notifications}
        filter={filter}
        selectedNotifications={selectedNotifications}
        handleToggleSelection={handleToggleSelection}
        handleMarkAsRead={handleMarkAsRead}
        handleDeleteNotification={handleDeleteNotification}
      />

      {summary && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>{t("planning.legacy.notifications.displayed", { count: notifications.length })}</div>
          <div>{t("planning.legacy.notifications.footerSummary", { unread: summary.unread_count, total: summary.total_count })}</div>
        </div>
      )}
    </div>
  );
};

export default PlanningNotifications;
